import { useState, useRef, useCallback, useEffect } from 'react';

export interface MediaDevicesState {
  isCameraLoading: boolean;
  isMicLoading: boolean;
  cameraError: string | null;
  micError: string | null;
  cameraActive: boolean;
  micActive: boolean;
  isRearCamera: boolean;
  audioBlocked: boolean;
  stream: MediaStream | null;
  facingMode: 'user' | 'environment';
}

export const useMediaDevices = () => {
  const [state, setState] = useState<MediaDevicesState>({
    isCameraLoading: false,
    isMicLoading: false,
    cameraError: null,
    micError: null,
    cameraActive: false,
    micActive: false,
    isRearCamera: false,
    audioBlocked: false,
    stream: null,
    facingMode: 'user',
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const currentStreamRef = useRef<MediaStream | null>(null);

  // Initialize audio context for mobile
  const initializeAudioContext = useCallback(async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
        setState(prev => ({ ...prev, audioBlocked: false }));
      }
    } catch (error) {
      console.warn('Audio context initialization failed:', error);
      setState(prev => ({ ...prev, audioBlocked: true }));
    }
  }, []);

  // Check permissions status
  const checkPermissions = useCallback(async () => {
    try {
      if ('permissions' in navigator) {
        const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
        const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        
        setState(prev => ({
          ...prev,
          cameraActive: cameraPermission.state === 'granted' && !!currentStreamRef.current,
          micActive: micPermission.state === 'granted' && !!currentStreamRef.current,
        }));
      }
    } catch (error) {
      console.warn('Permission check failed:', error);
    }
  }, []);

  // Start camera with proper loading and error handling
  const startCamera = useCallback(async (facingMode: 'user' | 'environment' = 'user') => {
    console.log('Starting camera with facingMode:', facingMode);
    
    setState(prev => ({ 
      ...prev, 
      isCameraLoading: true, 
      cameraError: null,
      facingMode
    }));

    try {
      // Stop existing stream first
      if (currentStreamRef.current) {
        console.log('MediaDevices: Stopping existing stream');
        currentStreamRef.current.getTracks().forEach(track => track.stop());
        currentStreamRef.current = null;
      }

      // Clear video element
      if (videoRef.current) {
        console.log('MediaDevices: Clearing video element');
        videoRef.current.srcObject = null;
      }

      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      };

      console.log('MediaDevices: Requesting camera with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      currentStreamRef.current = stream;
      
      console.log('Camera stream obtained successfully');

      if (videoRef.current) {
        console.log('MediaDevices: Setting video properties and srcObject');
        
        // Set video properties BEFORE setting srcObject
        videoRef.current.autoplay = true;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        videoRef.current.controls = false;
        videoRef.current.setAttribute('webkit-playsinline', 'true');
        
        // Add comprehensive video event listeners for debugging
        videoRef.current.onloadeddata = () => console.log('📹 onLoadedData');
        videoRef.current.oncanplay = () => {
          console.log('✅ onCanPlay', videoRef.current?.videoWidth, videoRef.current?.videoHeight);
        };
        videoRef.current.onplay = () => console.log('▶️ onPlay');
        videoRef.current.onerror = (e) => console.error('❌ Video error', e);
        videoRef.current.onloadedmetadata = () => {
          console.log('📊 Video metadata loaded:', {
            width: videoRef.current?.videoWidth,
            height: videoRef.current?.videoHeight,
            readyState: videoRef.current?.readyState
          });
        };
        
        // Set srcObject and force load
        videoRef.current.srcObject = stream;
        videoRef.current.load();
        
        // Multiple attempts to play video
        const playVideo = async () => {
          try {
            await videoRef.current.play();
            console.log('✅ Video play successful');
          } catch (err) {
            console.error("❌ Play failed:", err);
            console.log('Video state:', {
              readyState: videoRef.current?.readyState,
              paused: videoRef.current?.paused,
              muted: videoRef.current?.muted,
              autoplay: videoRef.current?.autoplay
            });
            
            // Try again after a short delay
            setTimeout(() => {
              videoRef.current?.play().catch(e => console.error("❌ Retry play failed:", e));
            }, 100);
          }
        };
        
        // Initial play attempt
        playVideo();
        
        // Also try to play when metadata is loaded
        videoRef.current.onloadedmetadata = () => {
          console.error("❌ Play failed:", err);
          console.log('Video state:', {
            readyState: videoRef.current?.readyState,
            paused: videoRef.current?.paused,
            muted: videoRef.current?.muted,
            autoplay: videoRef.current?.autoplay
          });
          playVideo();
        };
      }

      setState(prev => ({
        ...prev,
        isCameraLoading: false,
        cameraActive: true,
        isRearCamera: facingMode === 'environment',
        micActive: true,
        stream,
        cameraError: null,
        micError: null,
      }));

      console.log('Camera started successfully');
      
      // Initialize audio context on first user interaction
      await initializeAudioContext();

    } catch (error) {
      console.error('Camera access failed:', error.name, error.message);
      console.error('MediaDevices: Full camera error:', error);
      
      let errorMessage = 'Camera access denied. Please enable camera permissions in your browser settings.';
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Camera permission denied. Please allow camera access and refresh the page.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No camera found. Please ensure your device has a camera.';
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Camera is being used by another application. Please close other apps and try again.';
        } else if (error.name === 'OverconstrainedError') {
          errorMessage = 'Camera constraints not supported. Trying with basic settings...';
          // Fallback with basic constraints
          try {
            console.log('MediaDevices: Trying fallback constraints');
            const basicStream = await navigator.mediaDevices.getUserMedia({ 
              video: { facingMode }, 
              audio: true 
            });
            currentStreamRef.current = basicStream;
            if (videoRef.current) {
              videoRef.current.srcObject = basicStream;
              await videoRef.current.play();
            }
            console.log('Camera started with basic constraints');
            setState(prev => ({
              ...prev,
              isCameraLoading: false,
              cameraActive: true,
              isRearCamera: facingMode === 'environment',
              micActive: true,
              stream: basicStream,
              cameraError: null,
            }));
            return;
          } catch (fallbackError) {
            console.error('MediaDevices: Fallback also failed:', fallbackError);
            errorMessage = 'Camera not available with current settings.';
          }
        }
      }

      setState(prev => ({
        ...prev,
        isCameraLoading: false,
        cameraError: errorMessage,
        cameraActive: false,
        micActive: false,
      }));
    }
  }, [initializeAudioContext]);

  // Toggle camera facing mode
  const toggleCamera = useCallback(async () => {
    const newFacingMode = state.facingMode === 'user' ? 'environment' : 'user';
    console.log('Toggling camera from', state.facingMode, 'to', newFacingMode);
    await startCamera(newFacingMode);
  }, [state.facingMode, startCamera]);

  // Start microphone only
  const startMicrophone = useCallback(async () => {
    setState(prev => ({ ...prev, isMicLoading: true, micError: null }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      setState(prev => ({
        ...prev,
        isMicLoading: false,
        micActive: true,
        micError: null,
      }));

      await initializeAudioContext();

      return stream;
    } catch (error) {
      console.error('Microphone access failed:', error);
      
      let errorMessage = 'Microphone access denied. Please enable microphone permissions.';
      
      if (error instanceof Error && error.name === 'NotAllowedError') {
        errorMessage = 'Microphone permission denied. Please allow microphone access and refresh the page.';
      }

      setState(prev => ({
        ...prev,
        isMicLoading: false,
        micError: errorMessage,
        micActive: false,
      }));

      throw error;
    }
  }, [initializeAudioContext]);

  // Play audio with mobile compatibility
  const playAudio = useCallback(async (audioUrl: string) => {
    try {
      console.log('Attempting to play audio:', audioUrl);
      
      // Ensure audio context is resumed
      await initializeAudioContext();

      const audio = new Audio(audioUrl);
      
      // Set audio properties for mobile compatibility
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      
      // Add event listeners for debugging
      audio.addEventListener('loadstart', () => console.log('Audio loading started'));
      audio.addEventListener('canplay', () => console.log('Audio can play'));
      audio.addEventListener('error', (e) => console.error('Audio error:', e));
      
      // Handle mobile autoplay restrictions
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('Audio played successfully');
      }
      
      setState(prev => ({ ...prev, audioBlocked: false }));
      
    } catch (error) {
      console.error('Audio playback failed:', error.name, error.message);
      setState(prev => ({ ...prev, audioBlocked: true }));
      
      // Show user-friendly message for audio issues
      throw new Error('Audio playback blocked. Please tap anywhere to enable voice feedback.');
    }
  }, [initializeAudioContext]);

  // Stop all media
  const stopMedia = useCallback(() => {
    if (currentStreamRef.current) {
      currentStreamRef.current.getTracks().forEach(track => track.stop());
      currentStreamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setState(prev => ({
      ...prev,
      cameraActive: false,
      micActive: false,
      stream: null,
    }));
  }, []);

  // Check permissions on mount
  useEffect(() => {
    checkPermissions();
  }, [checkPermissions]);

  return {
    ...state,
    videoRef,
    startCamera,
    startMicrophone,
    toggleCamera,
    playAudio,
    stopMedia,
    initializeAudioContext,
  };
};